import { getLogoutPath, getPostAuthLandingPath, isLoginLandingPath } from '../auth-landing.js';
import { clearToken, getStoredUser, storeUser, api } from '../api.js';
import { clearLegacyBatchJobPersistence, getState, setStateBulk } from '../state.js';

function switchAuthTab(tabName) {
  document.querySelectorAll('.auth-tab').forEach((tab) => {
    const isActive = tab.dataset.tab === tabName;
    tab.classList.toggle('active', isActive);
    tab.setAttribute('aria-selected', String(isActive));
    tab.tabIndex = isActive ? 0 : -1;
  });
  document.getElementById('auth-form-login').hidden = tabName !== 'login';
  document.getElementById('auth-form-register').hidden = tabName !== 'register';
}

function showAuthMessage(message, tone = 'error') {
  const errorEl = document.getElementById('auth-error');
  if (!errorEl) return;
  errorEl.textContent = message || '';
  errorEl.hidden = !message;
  errorEl.classList.toggle('is-success', tone === 'success');
}

export function showAuthModal() {
  document.getElementById('auth-modal').hidden = false;
  document.getElementById('app-wrapper').hidden = true;
}

function showApp() {
  document.getElementById('auth-modal').hidden = true;
  document.getElementById('app-wrapper').hidden = false;
}

function resolvePendingRestoreUrl() {
  let raw = '';
  try {
    raw = sessionStorage.getItem('pendingRestore') || '';
  } catch {
    return '';
  }
  if (!raw) return '';

  try {
    const pending = JSON.parse(raw);
    const listingId = String(pending?.listingId || '').trim();
    if (!/^\d+$/.test(listingId)) {
      sessionStorage.removeItem('pendingRestore');
      return '';
    }
    const parsedStep = parseInt(pending?.step, 10);
    const step = Number.isFinite(parsedStep) && parsedStep >= 1 && parsedStep <= 4
      ? Math.max(parsedStep, 3)
      : 3;
    return `/listing.html?restoreId=${encodeURIComponent(listingId)}&step=${step}`;
  } catch {
    try { sessionStorage.removeItem('pendingRestore'); } catch { /* ignore */ }
    return '';
  }
}

function goHomeAfterAuth() {
  const pendingRestoreUrl = resolvePendingRestoreUrl();
  if (pendingRestoreUrl) {
    window.location.href = pendingRestoreUrl;
    return;
  }
  window.location.href = getPostAuthLandingPath();
}

function updateUserInfoUI() {
  const user = getState('currentUser');
  const userDropdown = document.getElementById('user-dropdown');
  const userName = document.getElementById('user-name');

  const isVisible = Boolean(user && userName);
  if (userDropdown) userDropdown.hidden = !isVisible;
  if (isVisible) {
    userName.textContent = user.name || user.email || '用户';
  }
}

function initUserDropdown({ onLogout }) {
  const userNameEl = document.getElementById('user-name');
  const userDropdown = document.getElementById('user-dropdown');
  const logoutBtn = document.getElementById('logout-btn');

  if (!userNameEl || !userDropdown) return;

  userNameEl.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    userDropdown.classList.toggle('open');
    userNameEl.setAttribute('aria-expanded', userDropdown.classList.contains('open') ? 'true' : 'false');
  });

  document.addEventListener('click', (event) => {
    if (userDropdown.classList.contains('open') && !userDropdown.contains(event.target)) {
      userDropdown.classList.remove('open');
      userNameEl.setAttribute('aria-expanded', 'false');
    }
  });

  if (logoutBtn) {
    logoutBtn.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      userDropdown.classList.remove('open');
      userNameEl.setAttribute('aria-expanded', 'false');
      clearToken();
      clearLegacyBatchJobPersistence();
      onLogout?.();
      try {
        localStorage.removeItem('listing_highestStep');
        localStorage.removeItem('listing_savedListingId');
        sessionStorage.removeItem('listing_batchJobId');
        sessionStorage.removeItem('listing_activeListingGenerationTaskId');
      } catch { /* ignore */ }
      setStateBulk({
        isLoggedIn: false,
        currentUser: null,
        batchJobId: '',
        activeListingGenerationTaskId: '',
      });
      window.location.assign(getLogoutPath());
    });
  }
}

async function initDemoLoginButton() {
  const panel = document.getElementById('demo-login-panel');
  const btn = document.getElementById('demo-login-btn');
  if (!panel || !btn) return;

  try {
    const res = await fetch('/api/demo/status', { credentials: 'same-origin' });
    if (!res.ok) return;
    const data = await res.json();
    if (!data?.demoMode) return;
  } catch {
    return;
  }

  panel.hidden = false;
  btn.addEventListener('click', async () => {
    showAuthMessage('');
    btn.disabled = true;
    btn.classList.add('is-loading');
    const textEl = btn.querySelector('.btn-text');
    if (textEl) textEl.textContent = '进入演示中...';
    try {
      const res = await fetch('/api/demo/login', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      let data = {};
      try { data = await res.json(); } catch { /* ignore */ }
      if (!res.ok) {
        throw new Error(data.message || data.error || `演示登录失败 (${res.status})`);
      }
      if (data.user) storeUser(data.user);
      setStateBulk({ isLoggedIn: true, currentUser: data.user || null });
      const next = new URLSearchParams(window.location.search).get('next');
      const redirect = (next && next.startsWith('/') && !next.startsWith('//'))
        ? next
        : (data.redirect || getPostAuthLandingPath());
      window.location.href = redirect;
    } catch (error) {
      showAuthMessage(error.message || '演示登录失败');
      btn.disabled = false;
      btn.classList.remove('is-loading');
      if (textEl) textEl.textContent = '一键演示登录';
    }
  });
}

async function loadRegisterGroups() {
  const select = document.getElementById('register-group-id');
  if (!select || select.dataset.loaded === '1') return;
  const setGroupOptions = (items) => select.replaceChildren(...items.map(({ value = '', label }) => {
    const option = new Option(label, value);
    return option;
  }));
  setGroupOptions([{ label: '加载分组中...' }]);
  try {
    const data = await api.authGroups();
    const groups = data.groups || [];
    if (!groups.length) {
      setGroupOptions([{ label: '暂无可加入分组' }]);
      select.disabled = true;
      return;
    }
    select.disabled = false;
    setGroupOptions([
      { label: '请选择分组' },
      ...groups.map((group) => ({ value: String(group.id ?? ''), label: String(group.name ?? '') })),
    ]);
    select.dataset.loaded = '1';
  } catch {
    setGroupOptions([{ label: '分组加载失败，请稍后重试' }]);
    select.disabled = true;
  }
}

export async function initAuth({ onLogout } = {}) {
  const storedUser = getStoredUser();
  const isDedicatedLoginPage = isLoginLandingPath(window.location.pathname);

  try {
    const data = await api.me();
    if (isDedicatedLoginPage) {
      window.location.replace(getPostAuthLandingPath());
      return false;
    }
    storeUser(data.user);
    setStateBulk({ isLoggedIn: true, currentUser: data.user });
    showApp();
    updateUserInfoUI();
  } catch {
    clearToken();
    setStateBulk({ isLoggedIn: false, currentUser: null });
    showAuthModal();
  }

  document.querySelectorAll('.auth-tab').forEach((tab) => {
    tab.addEventListener('click', () => {
      const tabName = tab.dataset.tab;
      switchAuthTab(tabName);
      showAuthMessage('');
      if (tabName === 'register') loadRegisterGroups();
    });
  });

  document.querySelector('.auth-tabs')?.addEventListener('keydown', (event) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const tabs = Array.from(document.querySelectorAll('.auth-tab'));
    const currentIndex = tabs.indexOf(document.activeElement);
    if (currentIndex < 0) return;
    event.preventDefault();
    const nextIndex = event.key === 'Home'
      ? 0
      : event.key === 'End'
        ? tabs.length - 1
        : (currentIndex + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    const nextTab = tabs[nextIndex];
    switchAuthTab(nextTab.dataset.tab);
    showAuthMessage('');
    if (nextTab.dataset.tab === 'register') loadRegisterGroups();
    nextTab.focus();
  });

  document.getElementById('auth-form-login')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const email = document.getElementById('login-email').value.trim();
    const password = document.getElementById('login-password').value;
    const submitBtn = document.getElementById('login-submit');

    showAuthMessage('');
    submitBtn.disabled = true;
    submitBtn.classList.add('is-loading');
    submitBtn.querySelector('.btn-text').textContent = '登录中...';

    try {
      const data = await api.login({ email, password });
      storeUser(data.user);
      setStateBulk({ isLoggedIn: true, currentUser: data.user });
      goHomeAfterAuth();
    } catch (error) {
      showAuthMessage(error.message || '登录失败');
    } finally {
      submitBtn.disabled = false;
      submitBtn.classList.remove('is-loading');
      submitBtn.querySelector('.btn-text').textContent = '登录';
    }
  });

  document.getElementById('forgot-password-link')?.addEventListener('click', () => {
    showAuthMessage('请联系管理员重置密码', 'info');
  });

  // Interview demo: show one-click login only when APP_DEMO_MODE is on.
  initDemoLoginButton().catch(() => {});

  document.getElementById('auth-form-register')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const name = document.getElementById('register-name').value.trim();
    const email = document.getElementById('register-email').value.trim();
    const password = document.getElementById('register-password').value;
    const groupId = document.getElementById('register-group-id')?.value || '';
    const submitBtn = document.getElementById('register-submit');

    showAuthMessage('');
    if (!groupId) {
      showAuthMessage('请选择要加入的分组');
      return;
    }
    submitBtn.disabled = true;
    submitBtn.classList.add('is-loading');
    submitBtn.querySelector('.btn-text').textContent = '注册中...';

    try {
      const data = await api.register({
        name,
        email,
        password,
        groupId: Number(groupId),
      });
      document.getElementById('auth-form-register')?.reset();
      const select = document.getElementById('register-group-id');
      if (select) select.value = '';
      switchAuthTab('login');
      showAuthMessage(data.message || '注册申请已提交，请等待审批', 'success');
    } catch (error) {
      showAuthMessage(error.message || '注册失败');
    } finally {
      submitBtn.disabled = false;
      submitBtn.classList.remove('is-loading');
      submitBtn.querySelector('.btn-text').textContent = '注册';
    }
  });

  initUserDropdown({ onLogout });
  return !isDedicatedLoginPage;
}
